import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Image as ImageIcon, Upload, X, Send, Clock, Eye, Repeat } from 'lucide-react';
import { fetchPosts, uploadMedia, createPost, publishPost, schedulePost, fetchIGAccount, fetchLivePosts } from '../../redux/instagramSlice';
import InstagramPostPreview from './InstagramPostPreview';
import RecurrencePicker from '../../components/social/RecurrencePicker';

const MEDIA_TYPES = ['IMAGE', 'VIDEO', 'REELS', 'STORIES', 'CAROUSEL'];

const STATUS_COLORS = {
  draft: 'bg-[#F3F2F2] text-[#444444]',
  scheduled: 'bg-[#D6EAFB] text-[#0176D3]',
  publishing: 'bg-[#FFF4E5] text-[#8C4B02]',
  published: 'bg-[#E3F3E8] text-[#036B3A]',
  failed: 'bg-[#FDE7E9] text-[#8E030F]',
  expired: 'bg-[#FDE7E9] text-[#8E030F]',
};

export default function InstagramContentPage() {
  const dispatch = useDispatch();
  const { posts, loading, error, account, livePosts, livePostsLoading } = useSelector((s) => s.instagram);
  const [mediaType, setMediaType] = useState('IMAGE');
  const [caption, setCaption] = useState('');
  const [mediaUrls, setMediaUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [recurrence, setRecurrence] = useState({ frequency: 'none' });
  const [recurError, setRecurError] = useState('');

  useEffect(() => {
    dispatch(fetchPosts());
    dispatch(fetchIGAccount());
    dispatch(fetchLivePosts());
  }, [dispatch]);

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const result = await dispatch(uploadMedia(file)).unwrap();
        setMediaUrls((prev) => [...prev, result.url]);
      }
    } catch {
      // error surfaced via redux `error` state
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = (url) => setMediaUrls((prev) => prev.filter((u) => u !== url));

  const resetComposer = () => {
    setCaption('');
    setMediaUrls([]);
    setScheduleAt('');
    setRecurrence({ frequency: 'none' });
  };

  const submit = async (publishNow) => {
    if (!mediaUrls.length) return;
    setRecurError('');
    if (!publishNow && scheduleAt && recurrence.frequency === 'weekly' && !recurrence.daysOfWeek?.length) {
      setRecurError('Pick at least one day of the week to repeat on');
      return;
    }
    if (!publishNow && scheduleAt && recurrence.frequency !== 'none' && recurrence.endType === 'on' && !recurrence.endDate) {
      setRecurError('Pick an end date for the recurrence');
      return;
    }
    setSubmitting(true);
    try {
      const post = await dispatch(createPost({ mediaType, caption, mediaUrls })).unwrap();
      if (publishNow) {
        await dispatch(publishPost(post._id));
      } else if (scheduleAt) {
        await dispatch(schedulePost({
          postId: post._id,
          scheduledAt: new Date(scheduleAt).toISOString(),
          recurrence: recurrence.frequency !== 'none'
            ? { ...recurrence, endDate: recurrence.endType === 'on' ? new Date(recurrence.endDate).toISOString() : undefined }
            : undefined,
        }));
      }
      resetComposer();
      dispatch(fetchPosts());
    } catch {
      // error surfaced via redux `error` state
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-5 space-y-4">
        <div className="flex items-center gap-2">
          <ImageIcon size={18} className="text-[#E1306C]" />
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">New Post</h2>
        </div>

        {error && <div className="text-[13px] text-[#BA0517]">{error}</div>}

        <div className="flex flex-wrap gap-2">
          {MEDIA_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => setMediaType(t)}
              className={`px-3 py-1.5 text-[12px] font-semibold rounded-full border transition-colors  whitespace-nowrap ${
                mediaType === t ? 'bg-[#E1306C] border-[#E1306C] text-white' : 'border-[#E5E5E5] text-[#5C5C5C] hover:border-[#E1306C]'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Write a caption…"
          rows={3}
          className="w-full border border-[#E5E5E5] rounded-[4px] px-3 py-2 text-[13px] resize-none focus:outline-none focus:border-[#0176D3]"
        />

        <div className="flex flex-wrap gap-2">
          {mediaUrls.map((url) => (
            <div key={url} className="relative w-20 h-20 rounded-[4px] overflow-hidden border border-[#E5E5E5]">
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button
                onClick={() => removeMedia(url)}
                className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <label className="w-20 h-20 flex items-center justify-center border border-dashed border-[#E5E5E5] rounded-[4px] cursor-pointer hover:border-[#0176D3] text-[#5C5C5C]">
            <input type="file" accept="image/*,video/*" multiple={mediaType === 'CAROUSEL'} onChange={onFileChange} className="hidden" disabled={uploading} />
            <Upload size={18} />
          </label>
        </div>
        {uploading && <div className="text-[12px] text-[#5C5C5C]">Uploading…</div>}

        <div className="pt-2 border-t border-[#E5E5E5] space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              className="border border-[#E5E5E5] rounded-[4px] px-3 py-2 text-[13px]"
            />
            <button
              onClick={() => submit(false)}
              disabled={submitting || !mediaUrls.length || !scheduleAt}
              className="flex items-center gap-1 border border-[#E5E5E5] hover:border-[#0176D3] text-[13px] font-semibold text-[#2E2E2E] px-4 py-2 rounded-[4px] disabled:opacity-50 whitespace-nowrap"
            >
              <Clock size={14} /> Schedule
            </button>
            <button
              onClick={() => setPreviewOpen(true)}
              disabled={!mediaUrls.length}
              className="flex items-center gap-1 border border-[#E5E5E5] hover:border-[#0176D3] text-[13px] font-semibold text-[#2E2E2E] px-4 py-2 rounded-[4px] disabled:opacity-50 ml-auto whitespace-nowrap"
            >
              <Eye size={14} /> Preview
            </button>
            <button
              onClick={() => submit(true)}
              disabled={submitting || !mediaUrls.length}
              className="flex items-center gap-1 bg-[#E1306C] hover:bg-[#c92a5f] text-white text-[13px] font-semibold px-4 py-2 rounded-[4px] disabled:opacity-50 whitespace-nowrap"
            >
              <Send size={14} /> {submitting ? 'Publishing…' : 'Publish Now'}
            </button>
          </div>

          {scheduleAt && (
            <>
              {recurError && <div className="text-[13px] text-[#BA0517]">{recurError}</div>}
              <RecurrencePicker onChange={setRecurrence} />
            </>
          )}
        </div>
      </div>

      <InstagramPostPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        mediaType={mediaType}
        caption={caption}
        mediaUrls={mediaUrls}
        account={account}
      />

      <div className="space-y-3">
        <h3 className="text-[13px] font-bold text-[#2E2E2E]">Live on Instagram</h3>
        <p className="text-[11px] text-[#5C5C5C] -mt-2">
          Fetched directly from Instagram — includes posts published outside this app.
        </p>
        {livePostsLoading && livePosts.length === 0 && <div className="text-[13px] text-[#5C5C5C]">Loading live posts…</div>}
        {!livePostsLoading && livePosts.length === 0 && (
          <div className="text-[13px] text-[#5C5C5C] bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-6 text-center">
            No live posts found on this Instagram account yet.
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {livePosts.map((m) => (
            <a
              key={m.id}
              href={m.permalink}
              target="ig_view_post"
              rel="noopener noreferrer"
              className="relative aspect-square overflow-hidden bg-[#E9ECF2] rounded-[4px] group block"
              title={m.caption?.slice(0, 120)}
            >
              {(m.media_type === 'VIDEO' ? m.thumbnail_url : m.media_url) ? (
                <img src={m.media_type === 'VIDEO' ? m.thumbnail_url : m.media_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#8E8E8E]"><ImageIcon size={24} /></div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
            </a>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-[13px] font-bold text-[#2E2E2E]">Drafts, Scheduled &amp; Failed (This App)</h3>
        {loading && posts.length === 0 && <div className="text-[13px] text-[#5C5C5C]">Loading posts…</div>}
        {!loading && posts.length === 0 && (
          <div className="text-[13px] text-[#5C5C5C] bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-6 text-center">
            No posts yet. Create your first post above.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {posts.map((p) => (
            <div key={p._id} className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex gap-3">
              {p.mediaUrls?.[0] && (
                <img src={p.mediaUrls[0]} alt="" className="w-16 h-16 rounded-[4px] object-cover flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-[#706E6B]">{p.mediaType}</span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[p.status] || 'bg-[#F3F2F2] text-[#444444]'}`}>
                    {p.status}
                  </span>
                </div>
                <div className="text-[13px] text-[#2E2E2E] mt-1 truncate">{p.caption || <span className="text-[#5C5C5C]">No caption</span>}</div>
                {p.scheduledAt && p.status === 'scheduled' && (
                  <div className="text-[11px] text-[#5C5C5C] mt-1">Scheduled: {new Date(p.scheduledAt).toLocaleString()}</div>
                )}
                {p.recurrence && p.recurrence.frequency !== 'none' && (
                  <div className="text-[11px] text-[#0176D3] mt-1 flex items-center gap-1">
                    <Repeat size={11} /> Repeats {p.recurrence.frequency} · #{p.occurrenceIndex}
                  </div>
                )}
                {p.error && <div className="text-[11px] text-[#BA0517] mt-1">{p.error}</div>}
                {p.status === 'draft' && (
                  <button
                    onClick={() => dispatch(publishPost(p._id))}
                    className="mt-2 text-[11px] font-semibold text-[#0176D3] hover:underline"
                  >
                    Publish now
                  </button>
                )}
                {p.status === 'failed' && (
                  <button
                    onClick={() => dispatch(publishPost(p._id))}
                    className="mt-2 text-[11px] font-semibold text-[#0176D3] hover:underline"
                  >
                    Retry — reuses the already-uploaded media, no re-upload needed
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
